import React from 'react';

const styles = {
  container: {
    marginBottom: '30px',
  },
  phaseHeader: {
    fontSize: '14px',
    fontWeight: '600',
    marginBottom: '10px',
    color: '#333333',
    fontFamily: 'Poppins, sans-serif',
  },
  phaseDescription: {
    fontSize: '12px',
    marginBottom: '18px',
    lineHeight: '1.4',
    color: '#666666',
    fontFamily: 'Poppins, sans-serif',
  },
  table: {
    width: '100%',
  },
  tableHeader: {
    display: 'flex',
    flexDirection: 'row',
    backgroundColor: '#f5f5f5',
    borderBottom: '1px solid #e0e0e0',
  },
  tableRow: {
    display: 'flex',
    flexDirection: 'row',
    borderBottom: '1px solid #f0f0f0',
    minHeight: '30px',
  },
  // Base styles
  baseText: {
    fontFamily: 'Poppins, sans-serif',
  },
  headerCell: {
    fontSize: '12px',
    fontWeight: '600',
    color: '#444444',
    padding: '8px',
    fontFamily: 'Poppins, sans-serif',
  },
  cell: {
    fontSize: '10px',
    color: '#333333',
    fontFamily: 'Poppins, sans-serif',
  },
  columnContent: {
    padding: '6px 4px',
  },
  competencyItem: {
    marginBottom: '4px',
    padding: '2px 6px 4px',
    borderBottom: '0.5px dashed #e0e0e0',
  },
  firstCompetencyItem: {
    paddingTop: '8px',
  },
  lastCompetencyItem: {
    marginBottom: '0',
    paddingBottom: '0',
    borderBottom: 'none',
  },
  // Column styles with colors
  abilityColumn: {
    width: '25%',
    backgroundColor: '#F8F9FA',
  },
  skillsColumn: {
    width: '18.75%',
    backgroundColor: '#E8F5E9',
  },
  knowledgeColumn: {
    width: '18.75%',
    backgroundColor: '#E3F2FD',
  },
  mindsetColumn: {
    width: '18.75%',
    backgroundColor: '#FFF3E0',
  },
  instrumentsColumn: {
    width: '18.75%',
    backgroundColor: '#F3E5F5',
  },
  // Header cell variants
  headerCellAbility: {
    backgroundColor: '#F8F9FA',
  },
  headerCellSkills: {
    backgroundColor: '#E8F5E9',
  },
  headerCellKnowledge: {
    backgroundColor: '#E3F2FD',
  },
  headerCellMindset: {
    backgroundColor: '#FFF3E0',
  },
  headerCellInstruments: {
    backgroundColor: '#F3E5F5',
  },
  // Ability cell specific
  abilityCell: {
    fontSize: '10px',
    color: '#333333',
    fontWeight: '500',
    fontFamily: 'Poppins, sans-serif',
  }
};

const PhaseGridWeb = ({ phase, description, data }) => {
  // Transform the data into the format needed for rendering
  const gridData = Object.entries(data).map(([ability, categories]) => ({
    ability,
    skills: categories.skills || [],
    knowledge: categories.knowledge || [],
    mindset: categories.mindset || [],
    instruments: categories.tool || [] // Note: 'tool' in API maps to 'instruments' in display
  }));

  return (
    <div style={styles.container}>
      <div style={styles.phaseHeader}>{phase}</div>
      <div style={styles.phaseDescription}>{description}</div>

      <div style={styles.table}>
        <div style={styles.tableHeader}>
          <div style={{...styles.headerCell, ...styles.headerCellAbility, ...styles.abilityColumn}}>Ability</div>
          <div style={{...styles.headerCell, ...styles.headerCellSkills, ...styles.skillsColumn}}>Skills</div>
          <div style={{...styles.headerCell, ...styles.headerCellKnowledge, ...styles.knowledgeColumn}}>Knowledge</div>
          <div style={{...styles.headerCell, ...styles.headerCellMindset, ...styles.mindsetColumn}}>Mindset</div>
          <div style={{...styles.headerCell, ...styles.headerCellInstruments, ...styles.instrumentsColumn}}>Instruments</div>
        </div>

        {gridData.length > 0 ? (
          gridData.map((row, index) => (
            <div key={index} style={styles.tableRow}>
              <div style={styles.abilityColumn}>
                <div style={{...styles.abilityCell, ...styles.columnContent}}>{row.ability}</div>
              </div>
              
              <div style={styles.skillsColumn}>
                {row.skills.map((skill, i) => (
                  <div 
                    key={i} 
                    style={{
                      ...styles.competencyItem,
                      ...(i === 0 ? styles.firstCompetencyItem : {}),
                      ...(i === row.skills.length - 1 ? styles.lastCompetencyItem : {})
                    }}
                  >
                    <div style={styles.cell}>{skill}</div>
                  </div>
                ))}
              </div>

              <div style={styles.knowledgeColumn}>
                {row.knowledge.map((item, i) => (
                  <div 
                    key={i} 
                    style={{
                      ...styles.competencyItem,
                      ...(i === 0 ? styles.firstCompetencyItem : {}),
                      ...(i === row.knowledge.length - 1 ? styles.lastCompetencyItem : {})
                    }}
                  >
                    <div style={styles.cell}>{item}</div>
                  </div>
                ))}
              </div>

              <div style={styles.mindsetColumn}>
                {row.mindset.map((item, i) => (
                  <div 
                    key={i} 
                    style={{
                      ...styles.competencyItem,
                      ...(i === 0 ? styles.firstCompetencyItem : {}),
                      ...(i === row.mindset.length - 1 ? styles.lastCompetencyItem : {})
                    }}
                  >
                    <div style={styles.cell}>{item}</div>
                  </div>
                ))}
              </div>

              <div style={styles.instrumentsColumn}>
                {row.instruments.map((item, i) => (
                  <div 
                    key={i} 
                    style={{
                      ...styles.competencyItem,
                      ...(i === 0 ? styles.firstCompetencyItem : {}),
                      ...(i === row.instruments.length - 1 ? styles.lastCompetencyItem : {})
                    }}
                  >
                    <div style={styles.cell}>{item}</div>
                  </div>
                ))}
              </div>
            </div>
          ))
        ) : (
          <div style={styles.tableRow}>
            <div style={{...styles.abilityColumn, flex: 1}}>
              <div style={{...styles.cell, ...styles.columnContent, textAlign: 'center'}}>
                No competencies selected for this phase
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default PhaseGridWeb;
